import React, { Fragment } from 'react';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  generateInstallmentAppPdf,
  generateSSNVerificationPdf,
  generatePaymentAdvicesPdf,
  generateAttorneyCompensationPdf,
  generateDEBNFormsPdf,
  fillFormBrowser
} from "./pdfGenerators";
import {
  PDFDocument,
  StandardFonts,
  PDFCheckBox,
  PDFTextField,
  PDFName,
  PDFDict,
  PDFNumber,
} from 'pdf-lib';
import { filingDebtors as getFilingDebtors } from '../../utils/debtors';
import MailingMatrixServiceSelector from '../components/MailingMatrixServiceSelector';
const API_URL2 = process.env.REACT_APP_BACKEND_URL
const token = localStorage.getItem('accessToken');
async function generateEnvelopesPdf(creditors) {
    if (!creditors || creditors.length === 0) return;
    const envDoc = await PDFDocument.create();
    const seen = new Set();
    creditors = creditors.filter(c => {
      const key = [c.name, c.street1, c.street2 || '', c.city, c.state, c.zip_code]
        .join('|');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    // 1. Load & embed your logo (adjust the path as needed)
    const logoUrl = '/images/logo.png';
    const logoBytes = await fetch(logoUrl).then(r => r.arrayBuffer());
    const logoImage = await envDoc.embedPng(logoBytes);
    const logoDims = logoImage.scale(0.1);
  
    // 2. Prepare font and layout constants
    const font = await envDoc.embedFont(StandardFonts.Helvetica);
    const fontSize = 10;
    const lineHeight = fontSize + 4;
    const envelopeSize = [9.5 * 72, 4.125 * 72]; // width × height in points
    const margin = 25;
  
    // Your return address lines
    const returnAddress = [
      'Cisowski Law',
      '3119 Bristol Hwy Ste 217',
      'Johnson City, TN 37601',
    ];
  
    // 3. One page per creditor
    for (const c of creditors) {
      const page = envDoc.addPage(envelopeSize);
      const { width, height } = page.getSize();
  
      // 3a. Draw logo in top-left
      page.drawImage(logoImage, {
        x: margin,
        y: height - margin - logoDims.height,
        width: logoDims.width,
        height: logoDims.height,
      });
  
      // 3b. Draw return address to the right of the logo
      let yPos = height - margin - 9;
      for (const line of returnAddress) {
        page.drawText(line, {
          x: margin + logoDims.width + 10,
          y: yPos,
          size: fontSize,
          font,
        });
        yPos -= fontSize + 2;
      }
  
      // draw recipient address centered vertically and horizontally
      const addrLines = [
        c.name,
        `${c.street1}${c.street2?.trim() ? `, ${c.street2}` : ''}`,
        `${c.city}, ${c.state} ${c.zip_code}`,
      ];
      // measure max line width
      const lineWidths = addrLines.map(line => font.widthOfTextAtSize(line, fontSize));
      const maxWidth = Math.max(...lineWidths);
      const startX = (width - maxWidth) / 2;
      let startY = height / 2 + (addrLines.length / 2) * lineHeight;
      for (const line of addrLines) {
        page.drawText(line, {
          x: startX,
          y: startY,
          size: fontSize,
          font,
        });
        startY -= lineHeight;
      }
    }
  
    // 4. Save & trigger download
    const pdfBytes = await envDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'envelopes.pdf';
    link.click();
  }
// Create a letter‑sized PDF listing served creditors
async function generateCreditorsListPdf(creditors) {
    // Create the document
    const listDoc = await PDFDocument.create();
  
    // Deduplicate by name & address
    const seen = new Set();
    creditors = creditors.filter(c => {
      const key = [c.name, c.street1, c.street2 || '', c.city, c.state, c.zip_code]
        .join('|');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  
    const helvetica = await listDoc.embedFont(StandardFonts.Helvetica);
    const pageSize = [612, 792]; // Letter size
    let page = listDoc.addPage(pageSize);
    const { width, height } = page.getSize();
    const margin = 50;
    let y = height - margin;
    const lineHeight = 12;
    const indent = margin + 10;
  
    for (const c of creditors) {
      // Name
      page.drawText(c.name, { x: margin, y, size: 10, font: helvetica });
      y -= lineHeight;
      // Address line 1
      page.drawText(
        `${c.street1}${c.street2?.trim() ? ', ' + c.street2 : ''}`,
        { x: indent, y, size: 10, font: helvetica }
      );
      y -= lineHeight;
      // City, State Zip
      page.drawText(
        `${c.city}, ${c.state} ${c.zip_code}`,
        { x: indent, y, size: 10, font: helvetica }
      );
      y -= lineHeight;
      // Method
      page.drawText(`Served via: ${c.method}`, { x: indent, y, size: 10, font: helvetica });
      y -= lineHeight * 1.5;
  
      if (y < margin) {
        page = listDoc.addPage(pageSize);
        y = height - margin;
      }
    }
  
    return listDoc;
  }

const API_URL = process.env.REACT_APP_API_URL;
function VoluntaryPetition() {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const { caseId } = useParams();
  const [petitionData, setPetitionData] = useState({
    debtors: [],
    chapter: '',
    first_name: '',
    second_name: '',
    last_name: '',
    aliases: [],
    ssn: '',
    street: '',
    street2: '',
    city: '',
    state: '',
    zip_code: '',
    county: '',
    mailing_street: '',
    mailing_street2: '',
    mailing_city: '',
    mailing_state: '',
    mailing_zip_code: '',
    prior_bankruptcies: [],
    intake_data: {},
    filing_fee_method: '',
    filing_fee_amount: '',
    debt_type: '',
    execution_date: new Date().toISOString().split('T')[0],
    alternative_venue_reason: '',
    being_evicted: false,
    liquidated_assets_in_7: false,
    pending_bankruptcies: [],
    amended: false,
    amendedReason: '',
    amendedCreditors: [],
    mailingMatrixService: null,
    total_attorney_fees: '',
    fees_paid_prior: '',
    share_fees: false,
    fees_source:'',
    fees_source_description:'',
    fees_to_be_paid_source:'',
    fees_to_be_paid_description:'',
  });
  // modal state for amended filing
  const [showAmendedModal, setShowAmendedModal] = useState(false);
  const [amendStep, setAmendStep] = useState(1);
  const [amendedReason, setAmendedReason] = useState('');
  const [amendedCreditors, setAmendedCreditors] = useState([]);
  const [mailingMatrixService, setMailingMatrixService] = useState(null);
  // creditor list
  const [creditors, setCreditors] = useState([]);
  // State for generating VOLP
  const [isGeneratingVOLP, setIsGeneratingVOLP] = useState(false);
  // Format SSN as XXX-XX-XXXX
  const formatSSN = val => {
    const digits = val.replace(/\D/g, '').slice(0, 9);
    if (digits.length < 4) return digits;
    if (digits.length < 6) return `${digits.slice(0,3)}-${digits.slice(3)}`;
    return `${digits.slice(0,3)}-${digits.slice(3,5)}-${digits.slice(5)}`;
  };
  async function handleVOLPGenerate(pData) {
    if (isGeneratingVOLP) return;
    setIsGeneratingVOLP(true);
    try {
      const VOLP = await fillFormBrowser(pData);
      if (VOLP instanceof Blob) {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(VOLP);
        link.download = '101 Voluntary Petition.pdf';
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        console.error('fillFormBrowser did not return a Blob');
        alert('Failed to generate the Voluntary Petition PDF.');
      }
    } catch (e) {
      console.error('VOLP generation error:', e);
      alert('Failed to generate the Voluntary Petition PDF.');
    } finally {
      setIsGeneratingVOLP(false);
    }
  }
  // Save handler
  const handleSave = async e => {
    e.preventDefault();
    const token = localStorage.getItem("accessToken");
    try {
      const res = await fetch(`${API_URL}/case/${caseId}/volp/`, {
        method: "PUT",
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(petitionData),
      });
      if (!res.ok) throw new Error("Save failed");
      alert("Changes saved");
    } catch (err) {
      console.error(err);
      alert(err.message);
    }
  };
  useEffect(() => {
    async function fetchData() {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(`${API_URL}/case/${caseId}/volp`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        console.error('Failed to fetch petition data');
        return;
      }
      const caseData = await response.json();
      const filingDebtors = getFilingDebtors(caseData.debtors);
      setPetitionData({
        debtors: filingDebtors,
        chapter: caseData.chapter,
        first_name: filingDebtors[0]?.first_name || '',
        second_name: filingDebtors[0]?.second_name || '',
        last_name: filingDebtors[0]?.last_name || '',
        aliases: filingDebtors[0]?.aliases || [],
        ssn: filingDebtors[0]?.ssn || '',
        street: filingDebtors[0]?.street || '',
        street2: filingDebtors[0]?.street2 || '',
        city: filingDebtors[0]?.city || '',
        state: filingDebtors[0]?.state || '',
        zip_code: filingDebtors[0]?.zip_code || '',
        county: filingDebtors[0]?.county || '',
        mailing_street: filingDebtors[0]?.mailing_street || '',
        mailing_street2: filingDebtors[0]?.mailing_street2 || '',
        mailing_city: filingDebtors[0]?.mailing_city || '',
        mailing_state: filingDebtors[0]?.mailing_state || '',
        mailing_zip_code: filingDebtors[0]?.mailing_zip_code || '',
        hazerdous_properties: caseData.hazerdous_properties || [],
        pending_bankruptcies: caseData.pending_bankruptcies || [],
        prior_bankruptcies: filingDebtors[0]?.prior_bankruptcies || [],
        intake_data: caseData.intake_data,
        filing_fee_method: caseData.filing_fee_method,
        filing_fee_amount: caseData.filing_fee_amount,
        debt_type: caseData.debt_type,
        execution_date: caseData.execution_date || new Date().toISOString().split('T')[0],
        alternative_venue_reason: caseData.alternative_venue_reason ?? '',
        creditor_count: caseData.creditor_count,
        case_number: caseData.case_number,
        total_debt_amount: caseData.total_debt_amount,
        real_property_total: caseData.real_property_total,
        personal_property_total: caseData.personal_property_total,
        being_evicted: caseData.being_evicted ?? false,
        liquidated_assets_in_7: caseData.liquidated_assets_in_7 ?? false,
        pays_rent: caseData.pays_rent ?? false,
        amended: false,
        amendedReason: '',
        amendedCreditors: [],
        fees_paid_prior: caseData.fees_paid_prior,
        share_fees: caseData.share_fees,
        fees_source:caseData.fees_source,
        fees_source_description:caseData.fees_source_description,
        fees_to_be_paid_source:caseData.fees_to_be_paid_source,
        fees_to_be_paid_description:caseData.fees_to_be_paid_description,
        total_attorney_fees:caseData.total_attorney_fees,
      });
      setCreditors(caseData.creditor_addresses || []);
    }
    fetchData();
  }, [caseId]);

  return (
    <>
      {/* Petition Header */}
      <div className="mb-6 p-6 bg-indigo-50 rounded-lg text-center">
      <h1 className="mt-4 text-2xl font-semibold text-indigo-800">
          Chapter {petitionData.chapter} Petition
        </h1>
        <div className="flex flex-col md:flex-row md:items-center md:justify-center md:space-x-6 space-y-4 md:space-y-0">
          {/* Chapter selector */}
          <div>
            <label className="block text-sm font-medium text-indigo-700">Chapter</label>
            <select
              value={petitionData.chapter}
              onChange={e => setPetitionData({ ...petitionData, chapter: e.target.value })}
              className="mt-1 block w-full md:w-32 border-indigo-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Select</option>
              <option value="7">7</option>
              <option value="13">13</option>
            </select>
          </div>
          {/* Execution date picker */}
          <div>
            <label className="block text-sm font-medium text-indigo-700">Execution Date</label>
            <input
              type="date"
              value={petitionData.execution_date || new Date().toISOString().split('T')[0]}
              onChange={e => setPetitionData({ ...petitionData, execution_date: e.target.value })}
              className="mt-1 block w-full md:w-48 border-indigo-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
      </div>
      <form onSubmit={handleSave} className="max-w-full mx-auto p-6 bg-white rounded-lg shadow space-y-8">
        {/* Case Information */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Filing Fee Method</label>
            <select
              value={petitionData.filing_fee_method}
              onChange={e => setPetitionData({ ...petitionData, filing_fee_method: e.target.value })}
              className="border border-gray-300 rounded px-3 py-2 w-full focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Method</option>
              <option value="with_petition">With Petition</option>
              <option value="installment">Installment</option>
              <option value="waiver">Waiver</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Debt Type</label>
            <select
              value={petitionData.debt_type}
              onChange={e => setPetitionData({ ...petitionData, debt_type: e.target.value })}
              className="border border-gray-300 rounded px-3 py-2 w-full focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Debt Type</option>
              <option value="business">Business</option>
              <option value="consumer">Consumer</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Pays Rent</label>
            <select
              value={petitionData.pays_rent ? 'yes' : 'no'}
              onChange={e =>
                setPetitionData({
                  ...petitionData,
                  pays_rent: e.target.value === 'yes',
                })
              }
              className="border border-gray-300 rounded px-3 py-2 w-full focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select...</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        </div>

     {/* Debtor Information */}
        <div
          className={`mt-6 ${
            petitionData.debtors.length > 1
              ? 'grid grid-cols-1 sm:grid-cols-2 gap-4 justify-items-center'
              : 'flex justify-center'
          }`}
        >
          {petitionData.debtors.map((debtor, idx) => (
            <div key={debtor.id} className="bg-gray-50 border border-gray-200 rounded p-4 space-y-4 w-full max-w-[600px]">
              <h3 className="text-lg font-semibold">Debtor {idx + 1}</h3>
              {/* Name fields */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {['first_name','second_name','last_name'].map(field => (
                  <div key={field}>
                    <label className="block text-sm font-medium text-gray-700">
                      {field.replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())}
                    </label>
                    <input
                      value={debtor[field] || ''}
                      onChange={e => {
                        const newDs = [...petitionData.debtors];
                        newDs[idx][field] = e.target.value;
                        setPetitionData({ ...petitionData, debtors: newDs });
                      }}
                      className="border border-gray-300 rounded px-3 py-2 w-full focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))}
              </div>

              {/* SSN field */}
              <div>
                <label className="block text-sm font-medium text-gray-700">SSN</label>
                <input
                  value={debtor.ssn || ''}
                  onChange={e => {
                    const newDs = [...petitionData.debtors];
                    newDs[idx].ssn = formatSSN(e.target.value);
                    setPetitionData({ ...petitionData, debtors: newDs });
                  }}
                  className="border border-gray-300 rounded px-3 py-2 w-full focus:ring-2 focus:ring-blue-500"
                />
              </div>

             {/* Aliases */}
             <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Aliases</label>
                <Fragment>
                    <button
                    type="button"
                    onClick={() => {
                        const newDs = [...petitionData.debtors];
                        newDs[idx].aliases = [...(newDs[idx].aliases || []), { first_name: '', second_name: '', last_name: '', alias_type: '' }];
                        setPetitionData({ ...petitionData, debtors: newDs });
                    }}
                    className="text-sm text-blue-600 hover:underline mb-2"
                    >
                    + Add Alias
                    </button>
                    {(debtor.aliases || []).map((a, aidx) => (
                    <div key={aidx} className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-center">
                        {['first_name','second_name','last_name','alias_type'].map(field => (
                        field === 'alias_type' ? (
                            <select
                            key={field}
                            value={a[field] || ''}
                            onChange={e => {
                                const newDs = [...petitionData.debtors];
                                newDs[idx].aliases[aidx][field] = e.target.value;
                                setPetitionData({ ...petitionData, debtors: newDs });
                            }}
                            className="border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
                            >
                            <option value="">Select type</option>
                            {['aka','fka','dba','fdba','pdba','pka','ta'].map(type => (
                                <option key={type} value={type}>{type.toUpperCase()}</option>
                            ))}
                            </select>
                        ) : (
                            <input
                            key={field}
                            placeholder={field.replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())}
                            value={a[field] || ''}
                            onChange={e => {
                                const newDs = [...petitionData.debtors];
                                newDs[idx].aliases[aidx][field] = e.target.value;
                                setPetitionData({ ...petitionData, debtors: newDs });
                            }}
                            className="border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
                            />
                        )
                        ))}
                        <button
                        type="button"
                        onClick={() => {
                            const newDs = [...petitionData.debtors];
                            newDs[idx].aliases = newDs[idx].aliases.filter((_, i) => i !== aidx);
                            setPetitionData({ ...petitionData, debtors: newDs });
                        }}
                        className="text-sm text-red-600 hover:underline ml-2"
                        >
                        Remove
                        </button>
                    </div>
                    ))}
                </Fragment>
                </div>

              {/* Prior Bankruptcies */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                    Prior Bankruptcies
                </label>
                <button
                    type="button"
                    onClick={() => {
                    const newDs = [...petitionData.debtors];
                    newDs[idx].prior_bankruptcies = [
                        ...(newDs[idx].prior_bankruptcies || []),
                        { district: '', chapter: '', filing_date: '', case_number: '' }
                    ];
                    setPetitionData({ ...petitionData, debtors: newDs });
                    }}
                    className="text-sm text-blue-600 hover:underline mb-2"
                >
                    + Add Prior Bankruptcy
                </button>

                {(debtor.prior_bankruptcies || []).map((pb, pidx) => (
                    <div key={pidx} className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-center">
                    {/* District */}
                    <input
                        placeholder="District"
                        value={pb.district}
                        onChange={e => {
                        const newDs = [...petitionData.debtors];
                        newDs[idx].prior_bankruptcies[pidx].district = e.target.value;
                        setPetitionData({ ...petitionData, debtors: newDs });
                        }}
                        className="border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
                    />

                    {/* Chapter (7 or 13) */}
                    <select
                        value={pb.chapter}
                        onChange={e => {
                        const newDs = [...petitionData.debtors];
                        newDs[idx].prior_bankruptcies[pidx].chapter = e.target.value;
                        setPetitionData({ ...petitionData, debtors: newDs });
                        }}
                        className="border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">Select chapter</option>
                        <option value="7">7</option>
                        <option value="13">13</option>
                    </select>

                    {/* Filing Date (native date picker) */}
                    <input
                        type="date"
                        value={pb.filing_date}
                        onChange={e => {
                        const newDs = [...petitionData.debtors];
                        newDs[idx].prior_bankruptcies[pidx].filing_date = e.target.value;
                        setPetitionData({ ...petitionData, debtors: newDs });
                        }}
                        className="border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
                    />

                    {/* Case Number */}
                    <input
                        placeholder="Case Number"
                        value={pb.case_number}
                        onChange={e => {
                        const newDs = [...petitionData.debtors];
                        newDs[idx].prior_bankruptcies[pidx].case_number = e.target.value;
                        setPetitionData({ ...petitionData, debtors: newDs });
                        }}
                        className="border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
                    />

                    {/* Remove button */}
                    <button
                        type="button"
                        onClick={() => {
                        const newDs = [...petitionData.debtors];
                        newDs[idx].prior_bankruptcies = newDs[idx].prior_bankruptcies.filter((_, i) => i !== pidx);
                        setPetitionData({ ...petitionData, debtors: newDs });
                        }}
                        className="text-sm text-red-600 hover:underline ml-2"
                    >
                        Remove
                    </button>
                    </div>
                ))}
                </div>
              {/* Address fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {['street','city','state','zip_code'].map(field => (
                  <div key={field}>
                    <label className="block text-sm font-medium text-gray-700">
                      {field.replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())}
                    </label>
                    <input
                      value={debtor[field] || ''}
                      onChange={e => {
                        const newDs = [...petitionData.debtors];
                        newDs[idx][field] = e.target.value;
                        setPetitionData({ ...petitionData, debtors: newDs });
                      }}
                      className="border border-gray-300 rounded px-3 py-2 w-full focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))}
              </div>
              {/* Mailing fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {['mailing_street','mailing_city','mailing_state','mailing_zip_code'].map(field => (
                  <div key={field}>
                    <label className="block text-sm font-medium text-gray-700">
                      {field.replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())}
                    </label>
                    <input
                      value={debtor[field] || ''}
                      onChange={e => {
                        const newDs = [...petitionData.debtors];
                        newDs[idx][field] = e.target.value;
                        setPetitionData({ ...petitionData, debtors: newDs });
                      }}
                      className="border border-gray-300 rounded px-3 py-2 w-full focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))}
              </div>
              {/* DEBN Notice checkbox */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={debtor.debn_notice || false}
                  onChange={e => {
                    const newDs = [...petitionData.debtors];
                    newDs[idx].debn_notice = e.target.checked;
                    setPetitionData({ ...petitionData, debtors: newDs });
                  }}
                  className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                />
                <label className="text-sm text-gray-700">DEBN Notice</label>
              </div>
              {/* County text input */}
              <div>
                <label className="block text-sm font-medium text-gray-700">County</label>
                <input
                  type="text"
                  value={debtor.county || ''}
                  onChange={e => {
                    const newDs = [...petitionData.debtors];
                    newDs[idx].county = e.target.value;
                    setPetitionData({ ...petitionData, debtors: newDs });
                  }}
                  className="mt-1 block w-full border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            checked={petitionData.amended}
            onChange={e => {
              const checked = e.target.checked;
              setPetitionData({ ...petitionData, amended: checked });
              if (checked) {
                setShowAmendedModal(true);
                setAmendStep(1);
              }
            }}
            className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
          />
          <label className="ml-2 text-sm text-gray-700">Amended Filing</label>
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setShowAdvanced(true)}
            className="mb-4 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            Advanced Options
          </button>
        </div>
        <div className="text-right">
          <button
            type="submit"
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >
            Save Changes
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => handleVOLPGenerate(petitionData)}
            disabled={isGeneratingVOLP}
            className={`px-4 py-2 rounded text-white ${isGeneratingVOLP ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
            title={isGeneratingVOLP ? 'Generating...' : 'Generate Voluntary Petition'}
          >
            {isGeneratingVOLP ? 'Generating…' : 'Generate Voluntary Petition'}
          </button>

          {petitionData.filing_fee_method === "installment" && (
            <button
              type="button"
              onClick={() => generateInstallmentAppPdf(petitionData)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Installment Application
            </button>
          )}

          <button
            type="button"
            onClick={() => generateSSNVerificationPdf(petitionData)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            SSN Verification
          </button>
          <button
            type="button"
            onClick={() => generatePaymentAdvicesPdf(petitionData)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Payment Advices
          </button>
          <button
            type="button"
            onClick={() => generateAttorneyCompensationPdf(petitionData)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Attorney Disclosure
          </button>

          {/* DEBN forms for each debtor */}
          {petitionData.debtors.map((debtor, idx) => (
            <button
              key={debtor.id}
              type="button"
              onClick={() => generateDEBNFormsPdf(petitionData, debtor.is_primary)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              DEBN for Debtor {idx+1}
            </button>
          ))}
        </div>
      </form>
      {showAdvanced && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white rounded-lg shadow-lg max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Advanced Options</h3>
            <div className="space-y-4">
                {/* Alt Venue */}
                <div>
                <label className="block text-sm font-medium text-gray-700">
                    Alternative Venue Reason
                </label>
                <input
                    type="text"
                    value={petitionData.alternative_venue_reason || ''}
                    onChange={e =>
                    setPetitionData({ ...petitionData, alternative_venue_reason: e.target.value })
                    }
                    className="mt-1 block w-full border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
                </div>
                {/* Hazardous Properties */}
                {/* Hazardous Properties */}
                    <div>
                    <label className="block text-sm font-medium text-gray-700">Hazardous Properties</label>
                    <button
                        type="button"
                        onClick={() => {
                        const arr = petitionData.hazerdous_properties || [];
                        setPetitionData({
                            ...petitionData,
                            hazerdous_properties: [
                            ...arr,
                            { description: '', immediate_attention_reason: '', street1: '', street2: '', city: '', state: '', zip_code: '' }
                            ]
                        });
                        }}
                        className="text-sm text-blue-600 hover:underline mb-2"
                    >
                        + Add Hazardous Property
                    </button>
                    {(petitionData.hazerdous_properties || []).map((hp, idx) => (
                        <div key={idx} className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                        {[
                            'description',
                            'immediate_attention_reason',
                            'street1',
                            'street2',
                            'city',
                            'state',
                            'zip_code'
                        ].map(field => (
                            <input
                            key={field}
                            placeholder={field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                            value={hp[field] || ''}
                            onChange={e => {
                                const arr = [...petitionData.hazerdous_properties];
                                arr[idx][field] = e.target.value;
                                setPetitionData({ ...petitionData, hazerdous_properties: arr });
                            }}
                            className="border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
                            />
                        ))}
                        <button
                            type="button"
                            onClick={() => {
                            const arr = petitionData.hazerdous_properties.filter((_, i) => i !== idx);
                            setPetitionData({ ...petitionData, hazerdous_properties: arr });
                            }}
                            className="text-sm text-red-600 hover:underline col-span-full"
                        >
                            Remove Property
                        </button>
                        </div>
                    ))}
                    </div>
                    {/* Pending Bankruptcies */}
                        <div>
                        <label className="block text-sm font-medium text-gray-700">Pending Bankruptcies</label>
                        <button
                            type="button"
                            onClick={() => {
                            const arr = petitionData.pending_bankruptcies || [];
                            setPetitionData({
                                ...petitionData,
                                pending_bankruptcies: [
                                ...arr,
                                { debtor:'', relationship:'', district:'', chapter:'', filing_date:'', case_number:'' }
                                ]
                            });
                            }}
                            className="text-sm text-blue-600 hover:underline mb-2"
                        >
                            + Add Pending Bankruptcy
                        </button>
                        {(petitionData.pending_bankruptcies || []).map((pb, idx) => (
                            <div key={idx} className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
                            {[
                                'debtor',
                                'relationship',
                                'district',
                                'chapter',
                                'filing_date',
                                'case_number'
                            ].map(field => (
                                <input
                                key={field}
                                type={field === 'filing_date' ? 'date' : 'text'}
                                placeholder={field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                value={pb[field] || ''}
                                onChange={e => {
                                    const arr = [...petitionData.pending_bankruptcies];
                                    arr[idx][field] = e.target.value;
                                    setPetitionData({ ...petitionData, pending_bankruptcies: arr });
                                }}
                                className="border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
                                />
                            ))}
                            <button
                                type="button"
                                onClick={() => {
                                const arr = petitionData.pending_bankruptcies.filter((_, i) => i !== idx);
                                setPetitionData({ ...petitionData, pending_bankruptcies: arr });
                                }}
                                className="text-sm text-red-600 hover:underline col-span-full"
                            >
                                Remove Pending
                            </button>
                            </div>
                        ))}
                        </div>
                {/* Being Evicted */}
                <div className="flex items-center">
                <input
                    type="checkbox"
                    checked={petitionData.being_evicted}
                    onChange={e =>
                    setPetitionData({ ...petitionData, being_evicted: e.target.checked })
                    }
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">Being Evicted</label>
                </div>
                {/* Liquidated Assets */}
                <div className="flex items-center">
                <input
                    type="checkbox"
                    checked={petitionData.liquidated_assets_in_7}
                    onChange={e =>
                    setPetitionData({ ...petitionData, liquidated_assets_in_7: e.target.checked })
                    }
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">Liquidated Assets in Chapter 7</label>
                </div>
            </div>
            <div className="mt-6 text-right">
                <button
                type="button"
                onClick={() => setShowAdvanced(false)}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                >
                Close
                </button>
            </div>
            </div>
        </div>
        )}
      {showAmendedModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded shadow max-w-md w-full">
            {amendStep === 1 ? (
              <>
                <h3 className="text-lg font-semibold mb-2">Reason for Amendment</h3>
                <textarea
                  value={amendedReason}
                  onChange={e => setAmendedReason(e.target.value)}
                  className="w-full border rounded p-2"
                  rows={4}
                />
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => setAmendStep(2)}
                    className="px-4 py-2 bg-blue-600 text-white rounded"
                  >
                    Next
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold mb-2">Select Creditors to Serve</h3>
                <MailingMatrixServiceSelector
                  caseId={caseId}
                  value={mailingMatrixService}
                  onChange={service => {
                    setMailingMatrixService(service);
                    setAmendedCreditors(
                      service?.enabled
                        ? (service.recipients || []).map(recipient => ({ ...recipient, method: service.method || 'USM' }))
                        : []
                    );
                  }}
                />
                {!mailingMatrixService?.enabled && <>
                <button
                  onClick={() =>
                    setAmendedCreditors(creditors.map(c => ({ ...c, method: 'USM' })))
                  }
                  className="text-sm text-blue-600 mb-2"
                >
                  Select All as USM
                </button>
                <button
                  onClick={() => setAmendedCreditors([])}
                  className="ml-4 text-sm text-red-600 mb-2"
                >
                  Remove All
                </button>
                <div className="max-h-64 overflow-y-auto mb-4 border">
                  {creditors.map((c, idx) => {
                    const selected = amendedCreditors.find(ac => ac.name === c.name);
                    return (
                      <div key={idx} className="flex items-center justify-between p-2 border-b">
                        <div className="flex-1">
                          <div className="font-medium">{c.name}</div>
                          <div className="text-sm text-gray-500">
                            {c.street1}{c.street2?.trim() ? `, ${c.street2}` : ''}, {c.city}, {c.state} {c.zip_code}
                          </div>
                        </div>
                        <select
                          value={selected?.method || ''}
                          onChange={e => {
                            const method = e.target.value;
                            setAmendedCreditors(prev => {
                              const exists = prev.find(ac => ac.name === c.name);
                              console.log(amendedCreditors)
                              if (exists) {
                                return prev.map(ac =>
                                  ac.name === c.name ? { ...ac, method } : ac
                                );
                              }
                              return [...prev, { ...c, method }];
                            });
                          }}
                          className="border rounded px-2 py-1"
                        >
                          <option value="">None</option>
                          <option value="USM">USM</option>
                          <option value="Certified">Certified</option>
                        </select>
                      </div>
                    );
                  })}
                </div>
                </>}
                <div className="flex justify-between">
                  <button
                    onClick={() => setAmendStep(1)}
                    className="px-4 py-2 bg-gray-200 rounded"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => {
                      setShowAmendedModal(false);
                      setPetitionData({
                        ...petitionData,
                        amendedReason,
                        amendedCreditors,
                        mailingMatrixService,
                      });
                      console.log(petitionData)
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded"
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default VoluntaryPetition;
